import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Form } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { apiGet, apiUpload } from '../../services/api';

function countWords(text) {
  const plain = String(text || '').trim();
  if (!plain) return 0;
  return plain.split(/\s+/).filter(Boolean).length;
}

export default function RwmFlowApplyForm({ jobSlug }) {
  const { token, user } = useAuth();

  const hasProfileCv = Boolean(user?.cv_path);

  const [coverLetter, setCoverLetter] = useState('');
  const [cvFile, setCvFile] = useState(null);
  const [usingProfileCv, setUsingProfileCv] = useState(hasProfileCv);
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [genericError, setGenericError] = useState('');
  const [success, setSuccess] = useState(false);
  const [existingApplication, setExistingApplication] = useState(null);
  const [checkingExisting, setCheckingExisting] = useState(false);

  const wordCount = useMemo(() => countWords(coverLetter), [coverLetter]);
  const meetsWordMinimum = wordCount >= 50;

  useEffect(() => {
    if (user?.cv_path) {
      setUsingProfileCv(true);
    }
  }, [user?.cv_path]);

  useEffect(() => {
    if (!token || !user || user.role !== 'candidate' || !jobSlug) {
      setExistingApplication(null);
      return undefined;
    }

    let cancelled = false;
    setCheckingExisting(true);

    async function loadExistingApplication() {
      try {
        const res = await apiGet('/api/v1/candidate/applications', token);
        if (!res.ok) return;
        const json = await res.json();
        const match = (json.data ?? []).find((app) => app.job_slug === jobSlug);
        if (!cancelled) {
          setExistingApplication(match || null);
        }
      } finally {
        if (!cancelled) {
          setCheckingExisting(false);
        }
      }
    }

    loadExistingApplication();
    return () => {
      cancelled = true;
    };
  }, [token, user, jobSlug]);

  if (user && user.role !== 'candidate') {
    return null;
  }

  if (!user) {
    const returnTo = encodeURIComponent(`/jobs/${jobSlug}`);
    return (
      <div className="rwm-apply-form">
        <p className="small text-muted mb-3">
          Sign in as a candidate to apply on RemoteWorkMarket.
        </p>
        <Button
          variant="info"
          className="w-100 btn-lg text-white"
          as={Link}
          to={`/login?returnTo=${returnTo}`}
          data-testid="rwm-apply-login-link"
        >
          Log In to Apply
        </Button>
      </div>
    );
  }

  if (checkingExisting) {
    return (
      <div className="rwm-apply-form" data-testid="rwm-apply-checking-existing">
        <p className="small text-muted mb-0">Checking your application status…</p>
      </div>
    );
  }

  if (existingApplication) {
    return (
      <Alert variant="info" className="mb-0" data-testid="rwm-apply-already-applied">
        You have already applied to this job.
        {existingApplication.display_label ? (
          <>
            {' '}
            Current status:{' '}
            <strong data-testid="rwm-apply-already-applied-status">
              {existingApplication.display_label}
            </strong>
            .
          </>
        ) : null}{' '}
        Track it from your{' '}
        <Link to="/dashboard" className="alert-link">
          dashboard
        </Link>
        .
      </Alert>
    );
  }

  if (success) {
    return (
      <Alert variant="success" className="mb-0" data-testid="rwm-apply-success">
        Your application was submitted. Track it from your dashboard.
      </Alert>
    );
  }

  function handleUseDifferentCv() {
    setUsingProfileCv(false);
    setCvFile(null);
    setFieldErrors((prev) => ({ ...prev, cv: undefined }));
  }

  function handleUseProfileCv() {
    setUsingProfileCv(true);
    setCvFile(null);
    setFieldErrors((prev) => ({ ...prev, cv: undefined }));
  }

  function handleCvChange(e) {
    const file = e.target.files?.[0] ?? null;
    if (!file) {
      setCvFile(null);
      setFieldErrors((prev) => ({ ...prev, cv: undefined }));
      return;
    }

    const isPdf =
      file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
    if (!isPdf) {
      setCvFile(null);
      setFieldErrors((prev) => ({
        ...prev,
        cv: ['The CV must be a PDF file.'],
      }));
      e.target.value = '';
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setCvFile(null);
      setFieldErrors((prev) => ({
        ...prev,
        cv: ['The CV must not be larger than 5MB.'],
      }));
      e.target.value = '';
      return;
    }

    setFieldErrors((prev) => ({ ...prev, cv: undefined }));
    setCvFile(file);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setFieldErrors({});
    setGenericError('');

    if (!meetsWordMinimum) {
      setFieldErrors({
        cover_letter: ['Cover letter must be at least 50 words.'],
      });
      return;
    }

    if (!usingProfileCv && !cvFile) {
      setFieldErrors({ cv: ['Please upload your CV as a PDF.'] });
      return;
    }

    if (cvFile) {
      const isPdf =
        cvFile.type === 'application/pdf' || cvFile.name.toLowerCase().endsWith('.pdf');
      if (!isPdf) {
        setFieldErrors({ cv: ['The CV must be a PDF file.'] });
        return;
      }
      if (cvFile.size > 5 * 1024 * 1024) {
        setFieldErrors({ cv: ['The CV must not be larger than 5MB.'] });
        return;
      }
    }

    if (!token) {
      setGenericError('You must be logged in to apply.');
      return;
    }

    const formData = new FormData();
    formData.append('cover_letter', coverLetter);
    if (cvFile) {
      formData.append('cv', cvFile);
    }
    // If usingProfileCv and no cvFile, we send no 'cv' — backend uses profile cv_path

    setSubmitting(true);
    try {
      const res = await apiUpload(
        `/api/v1/jobs/${encodeURIComponent(jobSlug)}/apply`,
        formData,
        token
      );

      const json = await res.json().catch(() => ({}));

      if (res.status === 401) {
        setGenericError('Your session expired. Please log in again.');
        return;
      }

      if (res.status === 422) {
        setFieldErrors(json.errors || {});
        if (json.message && !json.errors) {
          setGenericError(json.message);
        }
        return;
      }

      if (!res.ok) {
        setGenericError('Could not submit your application. Please try again.');
        return;
      }

      setSuccess(true);
      setCoverLetter('');
      setCvFile(null);
    } catch {
      setGenericError('Could not submit your application. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Form className="rwm-apply-form" onSubmit={handleSubmit} noValidate data-testid="rwm-apply-form">
      <h3 className="h6 fw-semibold mb-3" style={{ color: 'var(--rwm-dark)' }}>
        Apply on RemoteWorkMarket
      </h3>
      <p className="small text-muted mb-3">
        Your application stays on RemoteWorkMarket.
      </p>

      {genericError ? (
        <Alert variant="danger" className="py-2 small" data-testid="rwm-apply-error">
          {genericError}
        </Alert>
      ) : null}

      <Form.Group className="mb-3" controlId="rwmApplyCoverLetter">
        <Form.Label>Cover letter</Form.Label>
        <Form.Control
          as="textarea"
          rows={6}
          value={coverLetter}
          onChange={(e) => setCoverLetter(e.target.value)}
          disabled={submitting}
          data-testid="rwm-apply-cover-letter"
          aria-describedby="rwm-apply-word-count"
        />
        <div
          id="rwm-apply-word-count"
          className={`small mt-1 ${meetsWordMinimum ? 'text-muted' : 'rwm-apply-form__word-count--low'}`}
          data-testid="rwm-apply-word-count"
        >
          {wordCount} / 50 words minimum
        </div>
        {fieldErrors.cover_letter ? (
          <div
            className="rwm-auth__field-error mt-1"
            data-testid="rwm-apply-cover-letter-error"
          >
            {fieldErrors.cover_letter[0]}
          </div>
        ) : null}
      </Form.Group>

      <Form.Group className="mb-3" controlId="rwmApplyCv">
        <Form.Label>CV (PDF, max 5MB)</Form.Label>

        {usingProfileCv ? (
          <div>
            <Badge bg="success" className="d-block mb-1" data-testid="rwm-apply-profile-cv-badge">
              ✓ Using your profile CV{user?.cv_filename ? ` — ${user.cv_filename}` : ''}
            </Badge>
            <button
              type="button"
              className="btn btn-link btn-sm p-0 text-muted"
              onClick={handleUseDifferentCv}
              disabled={submitting}
              data-testid="rwm-apply-use-different-cv"
            >
              Use a different CV instead
            </button>
          </div>
        ) : (
          <div>
            <Form.Control
              type="file"
              accept="application/pdf,.pdf"
              onChange={handleCvChange}
              disabled={submitting}
              data-testid="rwm-apply-cv-upload"
            />
            {hasProfileCv ? (
              <button
                type="button"
                className="btn btn-link btn-sm p-0 text-muted mt-1"
                onClick={handleUseProfileCv}
                disabled={submitting}
                data-testid="rwm-apply-use-profile-cv"
              >
                ← Use my profile CV
              </button>
            ) : null}
          </div>
        )}

        {fieldErrors.cv ? (
          <div className="rwm-auth__field-error mt-1" data-testid="rwm-apply-cv-error">
            {fieldErrors.cv[0]}
          </div>
        ) : null}
        {fieldErrors.job ? (
          <div className="rwm-auth__field-error mt-1">{fieldErrors.job[0]}</div>
        ) : null}
      </Form.Group>

      <Button
        type="submit"
        variant="info"
        className="w-100 btn-lg text-white"
        disabled={submitting}
        data-testid="rwm-apply-submit"
      >
        {submitting ? 'Submitting…' : 'Submit Application'}
      </Button>
    </Form>
  );
}
